import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { DEFAULT_CONFIG } from '../data/defaults';

const LOCAL_KEY = 'jerseybird_config';
const BUCKET    = 'landing';

// ─── helpers ─────────────────────────────────────────────────────────────────

function resolveUserId(externalId) {
  return externalId || localStorage.getItem('user_id') || null;
}

function storagePath(userId) {
  return `${userId}/landing-config.json`;
}

async function loadFromSupabase(userId) {
  if (!userId) {
    console.warn('[landing] load: userId não disponível');
    return null;
  }

  const { data, error } = await supabase.storage
    .from(BUCKET)
    .download(storagePath(userId));

  if (error) {
    if (error.message?.includes('404') || error.message?.includes('Not Found')) {
      // 404 — arquivo ainda não existe
    } else {
      console.error('[landing] load error:', error);
    }
    return null;
  }

  try {
    const text = await data.text();
    return JSON.parse(text);
  } catch (e) {
    console.error('[landing] load: erro ao parsear JSON:', e);
    return null;
  }
}

async function saveToSupabase(config, userId) {
  if (!userId) {
    console.warn('[landing] save: userId não disponível, não salvou');
    return;
  }

  const blob = new Blob([JSON.stringify(config)], { type: 'application/json' });

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(storagePath(userId), blob, {
      upsert: true,
      contentType: 'application/json',
    });

  if (error) {
    console.error('[landing] save error:', error);
  }
}

function saveLocal(config) {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(config)); } catch {}
}

function loadLocal() {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function deepMerge(base, override) {
  const result = { ...base };
  for (const key of Object.keys(override)) {
    if (
      override[key] !== null &&
      typeof override[key] === 'object' &&
      !Array.isArray(override[key]) &&
      typeof base[key] === 'object' &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key] ?? {}, override[key]);
    } else {
      result[key] = override[key];
    }
  }
  return result;
}

// ─── hook ────────────────────────────────────────────────────────────────────

/**
 * usePageConfig(externalUserId?)
 *
 * Caso 1 — usuário logado (sem passar userId):
 *   const { config } = usePageConfig()
 *   Usa localStorage.getItem('user_id') automaticamente.
 *
 * Caso 2 — link compartilhado (passando userId do dono):
 *   const { config } = usePageConfig(idClient)
 *   Carrega do Supabase assim que idClient estiver disponível.
 *   Não salva (sem permissão de escrita no storage de outro usuário).
 */
export function usePageConfig(externalUserId = null) {
  const [config, setConfig] = useState(() => {
    const local = loadLocal();
    return local ? deepMerge(DEFAULT_CONFIG, local) : DEFAULT_CONFIG;
  });
  const [loading, setLoading] = useState(true);

  // Recarrega sempre que o userId mudar (cobre o caso do link compartilhado
  // onde idClient chega depois da montagem)
  useEffect(() => {
    const userId = resolveUserId(externalUserId);
    setLoading(true);

    loadFromSupabase(userId).then((remote) => {
      if (remote) {
        const merged = deepMerge(DEFAULT_CONFIG, remote);
        setConfig(merged);
        // Só atualiza cache local se for o próprio usuário
        if (!externalUserId) saveLocal(merged);
      }
      setLoading(false);
    });

    // Expõe window function para recarregar passando userId externamente
    window.loadLandingConfig = (uid) => {
      const resolvedId = uid || resolveUserId(externalUserId);
      setLoading(true);
      loadFromSupabase(resolvedId).then((remote) => {
        if (remote) {
          const merged = deepMerge(DEFAULT_CONFIG, remote);
          setConfig(merged);
          saveLocal(merged);
        }
        setLoading(false);
      });
    };

    return () => { delete window.loadLandingConfig; };
  }, [externalUserId]); // re-executa quando idClient chegar

  const updateConfig = useCallback((partial) => {
    const userId = resolveUserId(externalUserId);
    setConfig((prev) => {
      const next = deepMerge(prev, partial);
      saveLocal(next);
      saveToSupabase(next, userId);
      return next;
    });
  }, [externalUserId]);

  const resetConfig = useCallback(() => {
    const userId = resolveUserId(externalUserId);
    const fresh = JSON.parse(JSON.stringify(DEFAULT_CONFIG));
    saveLocal(fresh);
    saveToSupabase(fresh, userId);
    setConfig(fresh);
  }, [externalUserId]);

  const exportConfig = useCallback(() => JSON.stringify(config, null, 2), [config]);

  const importConfig = useCallback((input) => {
    try {
      const userId = resolveUserId(externalUserId);
      const parsed = typeof input === 'string' ? JSON.parse(input) : input;
      const merged = deepMerge(DEFAULT_CONFIG, parsed);
      saveLocal(merged);
      saveToSupabase(merged, userId);
      setConfig(merged);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, [externalUserId]);

  return { config, updateConfig, resetConfig, exportConfig, importConfig, loading };
}